// src/pages/CalendarPage.jsx
// Full-page Team Calendar — Monthly + Daily views with 24-hour timeline.

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FaCalendarAlt, FaUmbrellaBeach, FaVideo, FaChevronLeft, FaChevronRight,
  FaCalendarCheck, FaCopy, FaCheck, FaCalendarPlus,
  FaClock, FaPlus, FaSearch, FaTimes, FaThLarge, FaListUl, FaEdit, FaSave,
} from "react-icons/fa";
import { getHolidayName, holidayTables } from "../config/holidays";
import { useMeetings } from "../context/MeetingsContext";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import styles from "./CalendarPage.module.css";

/* ── Constants ─────────────────────────────────────────────────── */
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const HOURS_24 = Array.from({ length: 24 }, (_, i) => i); // 0..23
const IST_TZ = "Asia/Kolkata";

const toDateKey = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** IST-aware date extraction */
const getISTDateParts = (input = new Date()) => {
  const date = input instanceof Date ? input : new Date(input);
  const parts = new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: IST_TZ,
  }).formatToParts(date);

  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value) - 1;
  const day = Number(parts.find((p) => p.type === "day")?.value);
  return { year, month, day };
};

/** IST hours + minutes from an ISO string */
const getISTTime = (isoStr) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone: IST_TZ,
  }).formatToParts(new Date(isoStr));
  const h = Number(parts.find(p => p.type === "hour").value);
  const m = Number(parts.find(p => p.type === "minute").value);
  return { hours: h, minutes: m };
};

const getISTTodayKey = () => {
  const { year, month, day } = getISTDateParts(new Date());
  return toDateKey(year, month, day);
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: IST_TZ }) +
    " " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: IST_TZ }) +
    " (IST)"
  );
};

const formatTimeOnly = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true, timeZone: IST_TZ,
  }) + " (IST)";
};

const splitPeriodForCard = (period = "") => {
  const text = String(period).trim();
  // Expected formats from holidays.js: "26 Jan" or "08-09 Nov"
  const m = text.match(/^(.+)\s([A-Za-z]{3,})$/);
  if (!m) return { dayPart: text, monPart: "" };
  return { dayPart: m[1], monPart: m[2].toUpperCase() };
};

/** Format hour index (0-23) to 12-hour label */
const hourLabel = (h) => {
  if (h === 0) return "12 AM";
  if (h < 12) return `${h} AM`;
  if (h === 12) return "12 PM";
  return `${h - 12} PM`;
};

const meetLink = (room) =>
  `${window.location.origin}/dashboard/meet?room=${encodeURIComponent(room)}`;

const buildGcalUrl = (m) => {
  const base = "https://calendar.google.com/calendar/render?action=TEMPLATE";
  const inviteLink = meetLink(m.room);
  const params = new URLSearchParams({
    text: m.title || "PrrepX Meet",
    details: `${m.description || "Meeting via PrrepX Meet."}\n\nJoin PrrepX Meet: ${inviteLink}`,
    location: inviteLink,
    dates: [
      new Date(m.startISO).toISOString().replace(/[-:.]/g, "").slice(0, 15) + "Z",
      new Date(m.endISO || m.startISO).toISOString().replace(/[-:.]/g, "").slice(0, 15) + "Z",
    ].join("/"),
    ctz: "Asia/Kolkata",
  });
  (m.guests || []).forEach((g) => { if (g.email) params.append("add", g.email); });
  return `${base}&${params.toString()}`;
};

/* ── Component ─────────────────────────────────────────────────── */
const CalendarPage = () => {
  const { currentUser, userData } = useAuth();
  const { meetings, updateMeeting } = useMeetings();

  const todayKey = getISTTodayKey();
  const now = new Date();
  const istNow = getISTDateParts(now);

  /* state */
  const [year, setYear] = useState(istNow.year);
  const [month, setMonth] = useState(istNow.month);
  const [tab, setTab] = useState("calendar"); // "calendar" | "meetings" | "holidays"
  const [calView, setCalView] = useState("monthly"); // "monthly" | "daily"
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD or null
  const [meetFilter, setMeetFilter] = useState("upcoming");
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingMeeting, setEditingMeeting] = useState(null); // meeting obj or null
  const [editForm, setEditForm] = useState({ title: "", description: "", startISO: "", endISO: "" });
  const [editSaving, setEditSaving] = useState(false);

  const timelineRef = useRef(null);

  /* derived data */
  const myName = userData?.name || currentUser?.email?.split("@")[0] || "";
  const myUid = currentUser?.uid || "";
  const userRole = userData?.role || "employee";
  const canEdit = (m) =>
    userRole === "superadmin" || userRole === "admin" || m.scheduledByUid === myUid || m.scheduledBy === myName;

  /* open daily view for a date */
  const openDaily = useCallback((dateKey) => {
    setSelectedDate(dateKey);
    setCalView("daily");
  }, []);

  /* nav */
  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const goToToday = () => {
    const parts = getISTDateParts(new Date());
    const key = toDateKey(parts.year, parts.month, parts.day);
    setYear(parts.year);
    setMonth(parts.month);
    setSelectedDate(key);
    setCalView("daily");
  };

  /* daily view: prev/next day */
  const dailyDate = selectedDate || todayKey;
  const prevDay = () => {
    const d = new Date(dailyDate + "T12:00:00+05:30");
    d.setDate(d.getDate() - 1);
    const parts = getISTDateParts(d.toISOString());
    const key = toDateKey(parts.year, parts.month, parts.day);
    setSelectedDate(key);
    setYear(parts.year);
    setMonth(parts.month);
  };
  const nextDay = () => {
    const d = new Date(dailyDate + "T12:00:00+05:30");
    d.setDate(d.getDate() + 1);
    const parts = getISTDateParts(d.toISOString());
    const key = toDateKey(parts.year, parts.month, parts.day);
    setSelectedDate(key);
    setYear(parts.year);
    setMonth(parts.month);
  };

  /* calendar grid */
  const firstDay = new Date(`${year}-${String(month + 1).padStart(2, '0')}-01T12:00:00+05:30`).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = useMemo(() => {
    const c = [];
    for (let i = 0; i < firstDay; i++) c.push(null);
    for (let d = 1; d <= daysInMonth; d++) c.push(d);
    while (c.length % 7 !== 0) c.push(null);
    return c;
  }, [firstDay, daysInMonth]);

  /* visible meetings — superadmin sees all, others see only meetings they organised or were invited to */
  const visibleMeetings = useMemo(() => {
    if (userRole === "superadmin") return meetings;
    return meetings.filter(m =>
      m.scheduledBy === myName ||
      m.scheduledByUid === myUid ||
      (m.guests || []).some(g => g.uid === myUid)
    );
  }, [meetings, userRole, myName, myUid]);

  /* meetings indexed by IST date */
  const meetingsByDate = useMemo(() => {
    const map = {};
    visibleMeetings.forEach((m) => {
      if (!m.startISO) return;
      const { year: my, month: mm, day: md } = getISTDateParts(m.startISO);
      const key = toDateKey(my, mm, md);
      if (!map[key]) map[key] = [];
      map[key].push(m);
    });
    return map;
  }, [visibleMeetings]);

  /* meetings for the selected / daily date */
  const dailyMeetings = meetingsByDate[dailyDate] || [];
  const dailyHoliday = getHolidayName(dailyDate) || null;

  // Monthly right sidebar: always show all meetings scheduled for today (IST),
  // then show upcoming meetings from future dates.
  const monthlySidebarMeetings = useMemo(() => {
    const list = visibleMeetings
      .filter((m) => m.startISO)
      .map((m) => {
        const parts = getISTDateParts(m.startISO);
        const key = toDateKey(parts.year, parts.month, parts.day);
        return { ...m, _dateKey: key };
      })
      .sort((a, b) => new Date(a.startISO) - new Date(b.startISO));

    const todayList = list.filter((m) => m._dateKey === todayKey);
    const upcomingList = list.filter((m) => {
      if (m._dateKey === todayKey) return false;
      return m.endISO ? new Date(m.endISO) >= now : new Date(m.startISO) >= now;
    });

    return [...todayList, ...upcomingList].slice(0, 10);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleMeetings, todayKey]);

  /* scroll daily timeline to "now" row on mount / date change */
  useEffect(() => {
    if (calView === "daily" && dailyDate === todayKey && timelineRef.current) {
      const { hours } = getISTTime(now.toISOString());
      const row = timelineRef.current.querySelector(`[data-hour="${hours}"]`);
      if (row) row.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calView, dailyDate]);

  /* now-indicator % for daily view */
  const nowIndicator = useMemo(() => {
    if (dailyDate !== todayKey) return null;
    const { hours, minutes } = getISTTime(now.toISOString());
    return { hour: hours, pct: (minutes / 60) * 100 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyDate, todayKey]);

  /* My Meetings tab data */
  const myMeetings = useMemo(() =>
    meetings.filter(m =>
      m.scheduledBy === myName ||
      m.scheduledByUid === myUid ||
      (m.guests || []).some(g => g.uid === myUid)
    ), [meetings, myName, myUid]);

  const filteredMeetings = useMemo(() => {
    let list;
    if (meetFilter === "upcoming") list = myMeetings.filter(m => m.endISO ? new Date(m.endISO) >= now : new Date(m.startISO) >= now);
    else if (meetFilter === "past") list = myMeetings.filter(m => m.endISO ? new Date(m.endISO) < now : new Date(m.startISO) < now);
    else list = [...myMeetings];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(m =>
        m.title?.toLowerCase().includes(q) ||
        m.scheduledBy?.toLowerCase().includes(q) ||
        m.description?.toLowerCase().includes(q) ||
        (m.guests || []).some(g => g.name?.toLowerCase().includes(q) || g.email?.toLowerCase().includes(q))
      );
    }

    return list.sort((a, b) =>
      meetFilter === "past"
        ? new Date(b.startISO) - new Date(a.startISO)
        : new Date(a.startISO) - new Date(b.startISO)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myMeetings, meetFilter, searchQuery]);

  const upcomingCount = myMeetings.filter(m => m.endISO ? new Date(m.endISO) >= now : new Date(m.startISO) >= now).length;

  /* Holidays tab data */
  const allHolidayEntries = useMemo(() => {
    return Object.values(holidayTables)
      .flatMap(entries => entries.map(e => ({ date: e.dates[0], name: e.name, period: e.period, days: e.days, category: e.category, dates: e.dates, count: e.count })))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, []);

  const holidayYear = useMemo(() => {
    const years = [...new Set(allHolidayEntries.map(e => e.date.slice(0, 4)))];
    return years.sort();
  }, [allHolidayEntries]);

  const [holYearFilter, setHolYearFilter] = useState(String(istNow.year));

  const filteredHolidays = useMemo(() =>
    allHolidayEntries.filter(e => e.date.startsWith(holYearFilter)),
  [allHolidayEntries, holYearFilter]);

  /* actions */
  const handleCopy = (room, id) => {
    navigator.clipboard.writeText(meetLink(room)).then(() => {
      setCopiedId(id);
      toast.success("Link copied!");
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const openEditModal = (m) => {
    setEditingMeeting(m);
    // Convert ISO to datetime-local value in IST
    const toLocalInput = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      const parts = new Intl.DateTimeFormat("en-CA", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata",
      }).formatToParts(d);
      const get = (t) => parts.find(p => p.type === t)?.value || "";
      return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
    };
    setEditForm({
      title: m.title || "",
      description: m.description || "",
      startISO: toLocalInput(m.startISO),
      endISO: toLocalInput(m.endISO),
    });
  };

  const handleEditSave = async () => {
    if (!editingMeeting) return;
    setEditSaving(true);
    try {
      const updates = {
        title: editForm.title,
        description: editForm.description,
      };
      // Convert datetime-local (IST) back to ISO UTC
      if (editForm.startISO) {
        const localStart = new Date(editForm.startISO + ":00+05:30");
        updates.startISO = localStart.toISOString();
      }
      if (editForm.endISO) {
        const localEnd = new Date(editForm.endISO + ":00+05:30");
        updates.endISO = localEnd.toISOString();
      }
      await updateMeeting(editingMeeting.id, updates);
      toast.success("Meeting updated!");
      setEditingMeeting(null);
    } catch {
      toast.error("Failed to update meeting");
    } finally {
      setEditSaving(false);
    }
  };

  /* Compute meeting positions for the daily timeline */
  const dailySlots = useMemo(() => {
    return dailyMeetings.map(m => {
      const start = getISTTime(m.startISO);
      const end = m.endISO ? getISTTime(m.endISO) : { hours: start.hours + 1, minutes: start.minutes };
      const startMin = start.hours * 60 + start.minutes;
      let endMin = end.hours * 60 + end.minutes;
      if (endMin <= startMin) endMin = startMin + 60; // fallback: 1 hr
      return { ...m, startMin, endMin, durationMin: endMin - startMin };
    });
  }, [dailyMeetings]);

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaCalendarAlt size={22} className={styles.headerIcon} />
          <div>
            <h1 className={styles.title}>Team Calendar</h1>
            <p className={styles.subtitle}>
              Meetings, holidays &amp; events — all in IST
            </p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.todayBtn} onClick={goToToday}>
            <FaClock size={12} /> Today
          </button>
          <Link to="/dashboard/meet" className={styles.scheduleBtn}>
            <FaPlus size={12} /> Schedule Meeting
          </Link>
        </div>
      </div>

      {/* Tab bar */}
      <div className={styles.tabBar}>
        <button className={`${styles.tabBtn} ${tab === "calendar" ? styles.tabActive : ""}`} onClick={() => setTab("calendar")}>
          <FaCalendarAlt size={13} /> Calendar
        </button>
        <button className={`${styles.tabBtn} ${tab === "meetings" ? styles.tabActive : ""}`} onClick={() => setTab("meetings")}>
          <FaCalendarCheck size={13} /> My Meetings
          {upcomingCount > 0 && <span className={styles.tabBadge}>{upcomingCount}</span>}
        </button>
        <button className={`${styles.tabBtn} ${tab === "holidays" ? styles.tabActive : ""}`} onClick={() => setTab("holidays")}>
          <FaUmbrellaBeach size={13} /> Holidays
        </button>
      </div>

      {/* ━━ CALENDAR TAB ━━ */}
      {tab === "calendar" && (
        <>
          {/* View switcher */}
          <div className={styles.viewSwitcher}>
            <button
              className={`${styles.viewBtn} ${calView === "monthly" ? styles.viewActive : ""}`}
              onClick={() => setCalView("monthly")}
            >
              <FaThLarge size={12} /> Monthly
            </button>
            <button
              className={`${styles.viewBtn} ${calView === "daily" ? styles.viewActive : ""}`}
              onClick={() => { setCalView("daily"); if (!selectedDate) setSelectedDate(todayKey); }}
            >
              <FaListUl size={12} /> Daily
            </button>
          </div>

          {/* ── MONTHLY VIEW ── */}
          {calView === "monthly" && (
            <div className={styles.calLayout}>
              <div className={styles.calMain}>
                {/* Month nav */}
                <div className={styles.monthNav}>
                  <button className={styles.navBtn} onClick={prevMonth}><FaChevronLeft size={14} /></button>
                  <span className={styles.monthLabel}>{MONTHS[month]} {year}</span>
                  <button className={styles.navBtn} onClick={nextMonth}><FaChevronRight size={14} /></button>
                </div>

                {/* Day headers */}
                <div className={styles.dayHeaders}>
                  {DAYS.map(d => <span key={d} className={`${styles.dayHeader} ${d === "Sun" ? styles.daySun : ""}`}>{d}</span>)}
                </div>

                {/* Grid */}
                <div className={styles.grid}>
                  {cells.map((d, idx) => {
                    if (!d) return <span key={`e${idx}`} className={styles.emptyCell} />;
                    const key = toDateKey(year, month, d);
                    const isToday = key === todayKey;
                    const holidayName = getHolidayName(key);
                  const isHoliday = !!holidayName;
                    const isSunday = new Date(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}T12:00:00+05:30`).getDay() === 0;
                    const dateMeetings = meetingsByDate[key] || [];
                    const hasMeeting = dateMeetings.length > 0;
                    const isSelected = key === selectedDate;
                    return (
                      <button
                        key={key}
                        className={`${styles.cell}
                          ${isToday ? styles.cellToday : ""}
                          ${isHoliday ? styles.cellHoliday : ""}
                          ${hasMeeting ? styles.cellMeeting : ""}
                          ${isSunday ? styles.cellSunday : ""}
                          ${isSelected ? styles.cellSelected : ""}
                        `}
                        onClick={() => openDaily(key)}
                        title={
                          isHoliday
                            ? `🎉 ${holidayName}`
                            : hasMeeting
                            ? dateMeetings.map(mt => `📹 ${mt.title}`).join("\n")
                            : undefined
                        }
                      >
                        <span className={styles.cellDay}>{d}</span>
                        <div className={styles.cellDots}>
                          {hasMeeting && <span className={styles.meetDot} />}
                          {isHoliday && <span className={styles.holDot} />}
                        </div>
                        {hasMeeting && <span className={styles.cellMeetCount}>{dateMeetings.length}</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className={styles.legend}>
                  <span><span className={styles.legendDotMeet} /> Meeting</span>
                  <span><span className={styles.legendDotHol} /> Holiday</span>
                  <span><span className={styles.legendDotToday} /> Today</span>
                  <span><span className={styles.legendDotSun} /> Sunday</span>
                </div>
              </div>

              {/* Sidebar — upcoming meetings */}
              <div className={styles.calSidebar}>
                <h3 className={styles.sideTitle}>Today's and Upcoming Meetings</h3>
                {monthlySidebarMeetings.length === 0 ? (
                  <div className={styles.sideEmpty}>
                    <FaCalendarCheck size={28} className={styles.sideEmptyIcon} />
                    <p>No meetings for today or upcoming dates.</p>
                    <Link to="/dashboard/meet" className={styles.sideSchedLink}><FaPlus size={10} /> Schedule one</Link>
                  </div>
                ) : (
                  <div className={styles.sideMeetList}>
                    {monthlySidebarMeetings.map(m => (
                        <MeetingCard key={m.id} m={m} copiedId={copiedId} onCopy={handleCopy} myName={myName} canEdit={canEdit(m)} onEdit={openEditModal} />
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── DAILY VIEW ── */}
          {calView === "daily" && (
            <div className={styles.dailyLayout}>
              {/* Day nav header */}
              <div className={styles.dailyHeader}>
                <button className={styles.navBtn} onClick={prevDay}><FaChevronLeft size={14} /></button>
                <div className={styles.dailyHeaderCenter}>
                  <span className={styles.dailyDateBig}>
                    {new Date(`${dailyDate}T12:00:00+05:30`).toLocaleDateString("en-IN", {
                      timeZone: IST_TZ, weekday: "long", day: "numeric", month: "long", year: "numeric",
                    })}
                  </span>
                  {dailyDate === todayKey && <span className={styles.dailyTodayBadge}>Today</span>}
                  {dailyHoliday && (
                    <span className={styles.dailyHolBadge}><FaUmbrellaBeach size={10} /> {dailyHoliday}</span>
                  )}
                </div>
                <button className={styles.navBtn} onClick={nextDay}><FaChevronRight size={14} /></button>
                <button
                  className={styles.backToMonthly}
                  onClick={() => setCalView("monthly")}
                  title="Back to monthly view"
                >
                  <FaThLarge size={12} /> Monthly
                </button>
              </div>

              {/* 24-hour timeline */}
              <div className={styles.timeline} ref={timelineRef}>
                {/* Hour grid rows (labels + lines) */}
                {HOURS_24.map((h) => (
                  <div key={h} className={styles.timeRow} data-hour={h}>
                    <div className={styles.timeLabel}>
                      <span className={styles.timeLabelText}>{hourLabel(h)}</span>
                    </div>
                    <div className={styles.timeSlot}>
                      {/* Now indicator */}
                      {nowIndicator && nowIndicator.hour === h && (
                        <div className={styles.nowLine} style={{ top: `${nowIndicator.pct}%` }}>
                          <span className={styles.nowDot} />
                          <span className={styles.nowLabel}>{formatTimeOnly(now.toISOString())}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Meetings overlay — positioned absolutely across the full 24h timeline */}
                <div className={styles.timelineOverlay}>
                  {dailySlots.map((s) => {
                    const ROW_HEIGHT = 64; // must match .timeRow min-height in CSS
                    const topPx = (s.startMin / 60) * ROW_HEIGHT;
                    const heightPx = Math.max((s.durationMin / 60) * ROW_HEIGHT, 28);
                    const isPast = s.endISO ? new Date(s.endISO) < now : new Date(s.startISO) < now;
                    return (
                      <div
                        key={s.id}
                        className={`${styles.timeEvent} ${isPast ? styles.timeEventPast : ""}`}
                        style={{ top: `${topPx}px`, height: `${heightPx}px` }}
                      >
                        <div className={styles.timeEventInner}>
                          <FaVideo size={10} className={styles.timeEventIcon} />
                          <div className={styles.timeEventInfo}>
                            <span className={styles.timeEventTitle}>{s.title}</span>
                            <span className={styles.timeEventTime}>
                              {formatTimeOnly(s.startISO)}
                              {s.endISO ? ` – ${formatTimeOnly(s.endISO)}` : ""}
                            </span>
                          </div>
                          {!isPast && (
                            <Link
                              to={`/dashboard/meet?room=${encodeURIComponent(s.room)}${s.lobbyEnabled ? "&lobby=1" : ""}`}
                              className={styles.timeEventJoin}
                              title="Join meeting"
                            >
                              Join
                            </Link>
                          )}
                          {canEdit(s) && (
                            <button
                              className={styles.timeEventEdit}
                              onClick={() => openEditModal(s)}
                              title="Edit meeting"
                            >
                              <FaEdit size={10} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Day summary sidebar */}
              <div className={styles.dailySidebar}>
                <h3 className={styles.sideTitle}>
                  {dailyMeetings.length} Meeting{dailyMeetings.length !== 1 ? "s" : ""} on this day
                </h3>
                {dailyMeetings.length === 0 ? (
                  <div className={styles.sideEmpty}>
                    <p>No meetings scheduled.</p>
                    <Link to="/dashboard/meet" className={styles.sideSchedLink}><FaPlus size={10} /> Schedule one</Link>
                  </div>
                ) : (
                  <div className={styles.sideMeetList}>
                    {dailyMeetings
                      .sort((a, b) => new Date(a.startISO) - new Date(b.startISO))
                      .map(m => (
                        <MeetingCard key={m.id} m={m} copiedId={copiedId} onCopy={handleCopy} myName={myName} canEdit={canEdit(m)} onEdit={openEditModal} expanded />
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ━━ MY MEETINGS TAB ━━ */}
      {tab === "meetings" && (
        <div className={styles.meetingsBody}>
          <div className={styles.meetToolbar}>
            <div className={styles.meetFilters}>
              {["upcoming", "past", "all"].map(f => (
                <button
                  key={f}
                  className={`${styles.meetFilterBtn} ${meetFilter === f ? styles.meetFilterActive : ""}`}
                  onClick={() => setMeetFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  {f === "upcoming" && upcomingCount > 0 && <span className={styles.filterBadge}>{upcomingCount}</span>}
                </button>
              ))}
            </div>
            <div className={styles.meetSearch}>
              <FaSearch size={12} className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search meetings..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              {searchQuery && (
                <button className={styles.clearSearch} onClick={() => setSearchQuery("")}><FaTimes size={10} /></button>
              )}
            </div>
          </div>

          {filteredMeetings.length === 0 ? (
            <div className={styles.meetEmpty}>
              <FaCalendarCheck size={36} className={styles.meetEmptyIcon} />
              <p>{searchQuery ? "No meetings match your search." : `No ${meetFilter} meetings for you.`}</p>
              {meetFilter === "upcoming" && !searchQuery && (
                <Link to="/dashboard/meet" className={styles.sideSchedLink}><FaPlus size={10} /> Schedule a meeting</Link>
              )}
            </div>
          ) : (
            <div className={styles.meetGrid}>
              {filteredMeetings.map(m => (
                <MeetingCard key={m.id} m={m} copiedId={copiedId} onCopy={handleCopy} myName={myName} canEdit={canEdit(m)} onEdit={openEditModal} expanded />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ━━ HOLIDAYS TAB ━━ */}
      {tab === "holidays" && (
        <div className={styles.holBody}>
          <div className={styles.holToolbar}>
            <h3 className={styles.holTitle}>Company Holidays</h3>
            <div className={styles.holYearBtns}>
              {holidayYear.map(y => (
                <button
                  key={y}
                  className={`${styles.holYearBtn} ${holYearFilter === y ? styles.holYearActive : ""}`}
                  onClick={() => setHolYearFilter(y)}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.holCount}>
            {filteredHolidays.reduce((sum, e) => sum + (e.count || 1), 0)} holiday days across {filteredHolidays.length} occasions
          </div>

          <div className={styles.holGrid}>
            {filteredHolidays.map((h) => {
              const d = new Date(h.date + "T12:00:00+05:30");
              const isPast = d < now;
              const periodParts = splitPeriodForCard(h.period);
              return (
                <div key={h.date} className={`${styles.holCard} ${isPast ? styles.holPast : ""}`}>
                  <div className={styles.holDateBlock}>
                    <span className={styles.holDayNum}>{periodParts.dayPart || getISTDateParts(d.toISOString()).day}</span>
                    <span className={styles.holMonAbbr}>{periodParts.monPart || MONTHS[getISTDateParts(d.toISOString()).month].slice(0, 3).toUpperCase()}</span>
                  </div>
                  <div className={styles.holInfo}>
                    <span className={styles.holName}>{h.name}</span>
                    <div className={styles.holMeta}>
                      <span className={styles.holWeekday}>{h.days || DAYS[d.getDay()]}</span>
                      {h.count > 1 && <span className={styles.holMulti}>{h.count} days</span>}
                      <span className={styles.holCategory}>{h.category}</span>
                    </div>
                  </div>
                  {isPast && <span className={styles.holPastBadge}>Past</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ━━ EDIT MEETING MODAL ━━ */}
      {editingMeeting && (
        <div className={styles.editOverlay} onClick={() => setEditingMeeting(null)}>
          <div className={styles.editModal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.editModalTitle}><FaEdit size={14} /> Edit Meeting</h3>
            <label className={styles.editLabel}>
              Title
              <input
                className={styles.editInput}
                value={editForm.title}
                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
              />
            </label>
            <label className={styles.editLabel}>
              Start Time (IST)
              <input
                type="datetime-local"
                className={styles.editInput}
                value={editForm.startISO}
                onChange={e => setEditForm(f => ({ ...f, startISO: e.target.value }))}
              />
            </label>
            <label className={styles.editLabel}>
              End Time (IST)
              <input
                type="datetime-local"
                className={styles.editInput}
                value={editForm.endISO}
                onChange={e => setEditForm(f => ({ ...f, endISO: e.target.value }))}
              />
            </label>
            <label className={styles.editLabel}>
              Description
              <textarea
                className={styles.editTextarea}
                rows={3}
                value={editForm.description}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
              />
            </label>
            <div className={styles.editActions}>
              <button className={styles.editCancel} onClick={() => setEditingMeeting(null)}>Cancel</button>
              <button className={styles.editSave} onClick={handleEditSave} disabled={editSaving}>
                <FaSave size={12} /> {editSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── MeetingCard sub-component ──────────────────────────────────── */
const MeetingCard = ({ m, copiedId, onCopy, myName, canEdit, onEdit, expanded }) => {
  const isPast = m.endISO ? new Date(m.endISO) < new Date() : new Date(m.startISO) < new Date();
  const isOrganizer = m.scheduledBy === myName;

  return (
    <div className={`${styles.mCard} ${isPast ? styles.mCardPast : ""}`}>
      <div className={styles.mCardTop}>
        <FaVideo size={13} className={styles.mCardIcon} />
        <div className={styles.mCardInfo}>
          <span className={styles.mCardTitle}>{m.title}</span>
          <span className={`${styles.mCardRole} ${isOrganizer ? styles.mCardOrg : styles.mCardGuest}`}>
            {isOrganizer ? "Organizer" : m.scheduledBy ? `by ${m.scheduledBy}` : ""}
          </span>
        </div>
      </div>
      <div className={styles.mCardTime}>
        <FaClock size={10} />
        <span>{formatDateTime(m.startISO)}</span>
        {m.endISO && <span className={styles.mCardDash}> – {formatTimeOnly(m.endISO)}</span>}
      </div>
      {expanded && m.description && (
        <p className={styles.mCardDesc}>{m.description}</p>
      )}
      {m.guests?.length > 0 && (
        <div className={styles.mCardGuests}>
          <div className={styles.guestAvatars}>
            {m.guests.slice(0, 5).map((g, i) => (
              <span
                key={g.uid || i}
                className={styles.guestChip}
                title={g.name || g.email || "Guest"}
              >
                {g.photoURL
                  ? <img src={g.photoURL} alt="" className={styles.guestImg} />
                  : <span className={styles.guestInitial}>{(g.name || g.email || "?")[0].toUpperCase()}</span>
                }
              </span>
            ))}
            {m.guests.length > 5 && (
              <span className={styles.guestMore} title={m.guests.slice(5).map(g => g.name || g.email).join(", ")}>
                +{m.guests.length - 5}
              </span>
            )}
          </div>
        </div>
      )}
      {!isPast && (
        <div className={styles.mCardActions}>
          <Link
            to={`/dashboard/meet?room=${encodeURIComponent(m.room)}${m.lobbyEnabled ? "&lobby=1" : ""}`}
            className={styles.mCardJoin}
          >
            <FaVideo size={10} /> Join
          </Link>
          <button className={styles.mCardCopy} onClick={() => onCopy(m.room, m.id)} title="Copy invite link">
            {copiedId === m.id ? <FaCheck size={10} /> : <FaCopy size={10} />}
            {copiedId === m.id ? "Copied!" : "Copy Link"}
          </button>
          <button
            className={styles.mCardGcal}
            onClick={() => window.open(buildGcalUrl(m), "_blank", "noopener,noreferrer")}
            title="Add to Google Calendar"
          >
            <FaCalendarPlus size={10} /> Google Cal
          </button>
          {canEdit && (
            <button className={styles.mCardEdit} onClick={() => onEdit(m)} title="Edit meeting">
              <FaEdit size={10} /> Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
